import {Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {ChooseAlternativeDialog} from '../../(sections)/AlternativesComparison/components/ChooseAlternativeDialog';
import {useChooseAlternative} from '../../(sections)/AlternativesComparison/hooks/useChooseAlternative';
import {OnlineOfferAlternative} from '../../api/types/onlineOfferAlternativesTypes';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {ThemeColorPath} from '../../theme/theme';
import {getAlternativeChoiceStatus} from '../../utils/getAlternativeChoiceStatus';
import {getAlternativeUnavailableReason} from '../../utils/getAlternativeUnavailableReason';
import {translateWithFallback as t} from '../../utils/translateWithFallback';
import {Button} from '../Button/Button';
import {CardOo} from '../CardOo/CardOo';

interface AlternativeChoiceCardProps {
  /** Renders nothing without one: a legacy offer has no alternative to choose. */
  alternative: OnlineOfferAlternative | null;
}

type CardState =
  | {type: 'chosen'; title: string; message: string}
  | {type: 'blocked'; tone: 'info' | 'warning'; message: string}
  | {type: 'choose'; warning: string | null};

const CHANGED_MESSAGE = () =>
  t('alternatives.notice.changed', 'The offer has changed. Please confirm your choice again.');

const BACKGROUND: Record<'chosen' | 'info' | 'warning' | 'choose', ThemeColorPath> = {
  chosen: 'semantic.bg.green',
  info: 'semantic.bg.subtle',
  warning: 'severity.warningCard',
  choose: 'accent.lightest',
};

/**
 * The non-binding choice of the alternative the page shows, so the customer can decide where they
 * read about the vehicle instead of only in the comparison.
 */
export function AlternativeChoiceCard(props: AlternativeChoiceCardProps) {
  const {offerAlternatives} = useGetOnlineOfferData();
  const choice = useChooseAlternative();

  const alternative = props.alternative;

  if (isNil(offerAlternatives) || isNil(alternative)) {
    return null;
  }

  const {customerChoice} = offerAlternatives;
  const choiceStatus = getAlternativeChoiceStatus(offerAlternatives, alternative);
  const unavailableReason = getAlternativeUnavailableReason(alternative);
  const isStaleChoiceOfThis =
    customerChoice?.alternativeId === alternative.id && !customerChoice.isCurrent;
  const hasOtherChoice =
    isNotNil(customerChoice) &&
    customerChoice.isCurrent &&
    customerChoice.alternativeId !== alternative.id;

  const getFailureMessage = () =>
    match(choice.failure)
      .with('CHANGED', () => CHANGED_MESSAGE())
      .with('NOT_SELECTABLE', () =>
        t(
          'alternatives.notice.notSelectable',
          'This alternative can no longer be selected. Please choose another one.'
        )
      )
      .with('FAILED', () =>
        t('alternatives.notice.failed', 'Your selection could not be saved. Please try again.')
      )
      .with(null, () => (isStaleChoiceOfThis ? CHANGED_MESSAGE() : null))
      .exhaustive();

  const getState = (): CardState => {
    if (choiceStatus === 'PREPARING') {
      return {
        type: 'chosen',
        title: t('alternatives.choice.preparing', 'Your adviser is preparing this vehicle'),
        message: t(
          'alternatives.notice.preparingMessage',
          'Your adviser will confirm the next steps with you.'
        ),
      };
    }
    if (choiceStatus === 'CHOSEN') {
      return {
        type: 'chosen',
        title: t('alternatives.choice.selectedByYou', 'Selected by you'),
        message: t(
          'alternatives.notice.selectedMessage',
          'Your adviser will confirm the next steps. Your selection does not create a reservation, order or contract.'
        ),
      };
    }
    if (offerAlternatives.cannotChooseReason === 'IN_PROCESSING') {
      return {
        type: 'blocked',
        tone: 'info',
        message: t(
          'alternatives.notice.inProcessing',
          'Your adviser is already processing your selection. Contact them to request a change.'
        ),
      };
    }
    if (isNotNil(unavailableReason)) {
      return {type: 'blocked', tone: 'warning', message: unavailableReason};
    }
    return {type: 'choose', warning: getFailureMessage()};
  };

  const state = getState();
  const background = match(state)
    .with({type: 'blocked'}, ({tone}) => BACKGROUND[tone])
    .otherwise(({type}) => BACKGROUND[type]);

  return (
    <CardOo backgroundColor={background}>
      {match(state)
        .with({type: 'chosen'}, ({title, message}) => (
          <VStack spacing={2}>
            <HStack spacing={2} align="center">
              <Icon value="good" size={5} color="text.successDark" />
              <Heading size={4} color="successDark">
                {title}
              </Heading>
            </HStack>
            <Text size="small" color="secondary">
              {message}
            </Text>
          </VStack>
        ))
        .with({type: 'blocked'}, ({message, tone}) => (
          <Text size="small" color={tone === 'warning' ? 'warningDark' : 'secondary'}>
            {message}
          </Text>
        ))
        .with({type: 'choose'}, ({warning}) => (
          <VStack spacing={4}>
            <VStack spacing={1}>
              <Heading size={4}>
                {t('alternatives.choice.cardTitle', 'Do you prefer this vehicle?')}
              </Heading>
              <Text size="small" color="secondary">
                {t(
                  'alternatives.choice.cardMessage',
                  'Let your adviser know. Your selection is non-binding: no contract, order or reservation.'
                )}
              </Text>
            </VStack>
            <Show when={isNotNil(warning)}>
              <Text size="small" color="warningDark" data-testid="alternativesNotice">
                {warning}
              </Text>
            </Show>
            <HStack>
              <Button
                variant="primary"
                isFullWidth
                title={
                  hasOtherChoice
                    ? t('alternatives.choice.selectInstead', 'Select this alternative instead')
                    : t('alternatives.choice.select', 'Select this alternative')
                }
                isDisabled={!offerAlternatives.canChoose}
                onClick={() => choice.request(alternative)}
                data-testid={`alternativeChoiceSelect-${alternative.letter}`}
              />
            </HStack>
          </VStack>
        ))
        .exhaustive()}
      <ChooseAlternativeDialog
        alternative={choice.pendingAlternative}
        isChoosing={choice.isChoosing}
        onConfirm={choice.confirm}
        onCancel={choice.cancel}
      />
    </CardOo>
  );
}
